import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message
} from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  MobileOutlined,
  ReloadOutlined,
  SendOutlined
} from "@ant-design/icons";

import {
  getPushStats,
  getPushHistory,
  sendPushNotification
} from "../../../api/pushNotification";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function PushNotificationManage() {
  const [form] = Form.useForm();

  const [stats, setStats] = useState({});
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);

  const loadStats = async () => {
    setLoadingStats(true);

    try {
      const res = await getPushStats();
      setStats(res?.data?.data || {});
      setFirebaseReady(!!res?.data?.firebase_ready);
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
        "Không tải được thống kê thiết bị"
      );
    } finally {
      setLoadingStats(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);

    try {
      const res = await getPushHistory(50);
      setHistory(
        Array.isArray(res?.data?.data)
          ? res.data.data
          : []
      );
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
        "Không tải được lịch sử thông báo"
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  const reloadAll = () => {
    loadStats();
    loadHistory();
  };

  useEffect(() => {
    reloadAll();
  }, []);

  const doSend = async (values, dryRun = false) => {
    setSending(true);

    try {
      const payload = {
        title: values.title,
        body: values.body,
        image: values.image || "",
        link:
          values.link ||
          "https://cuahangykhoa.com",
        type: values.type || "general",
        dry_run: dryRun
      };

      const res = await sendPushNotification(payload);
      const data = res?.data || {};

      if (dryRun) {
        Modal.success({
          title: "Kiểm tra Firebase thành công",
          content:
            `Đã kiểm tra ${data.total_tokens || 0} thiết bị. ` +
            `Thành công: ${data.success_count || 0}, ` +
            `thất bại: ${data.failure_count || 0}.`
        });
      } else {
        Modal.success({
          title: "Đã gửi thông báo",
          content:
            `Tổng thiết bị: ${data.total_tokens || 0}. ` +
            `Gửi thành công: ${data.success_count || 0}. ` +
            `Thất bại: ${data.failure_count || 0}.`
        });

        loadStats();
        loadHistory();
      }
    } catch (e) {
      Modal.error({
        title: dryRun
          ? "Không thể kiểm tra"
          : "Gửi thông báo thất bại",
        content:
          e?.response?.data?.message ||
          e?.message ||
          "Có lỗi xảy ra"
      });
    } finally {
      setSending(false);
    }
  };

  const confirmSend = async () => {
    try {
      const values = await form.validateFields();

      Modal.confirm({
        title: "Gửi thông báo đến người dùng?",
        content:
          "Thông báo sẽ được gửi đến tất cả thiết bị đang hoạt động.",
        okText: "Gửi ngay",
        cancelText: "Hủy",
        onOk: () => doSend(values, false)
      });
    } catch (_) {}
  };

  const testFirebase = async () => {
    try {
      const values = await form.validateFields();
      await doSend(values, true);
    } catch (_) {}
  };

  const columns = [
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: value => (
        <Text strong>{value}</Text>
      )
    },
    {
      title: "Nội dung",
      dataIndex: "mess",
      key: "mess",
      ellipsis: true
    },
    {
      title: "Loại",
      dataIndex: "type",
      key: "type",
      width: 130,
      render: value => (
        <Tag color="blue">
          {value || "general"}
        </Tag>
      )
    },
    {
      title: "Thời gian",
      dataIndex: "create_at",
      key: "create_at",
      width: 180
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 20
        }}
        align="center"
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <BellOutlined /> Thông báo đẩy
          </Title>

          <Text type="secondary">
            Gửi thông báo trực tiếp tới ứng dụng Cửa Hàng Y Khoa
          </Text>
        </div>

        <Button
          icon={<ReloadOutlined />}
          onClick={reloadAll}
          loading={loadingStats || loadingHistory}
        >
          Làm mới
        </Button>
      </Space>

      <Alert
        style={{ marginBottom: 20 }}
        type={firebaseReady ? "success" : "warning"}
        showIcon
        message={
          firebaseReady
            ? "Firebase Cloud Messaging đã sẵn sàng"
            : "Firebase Cloud Messaging chưa sẵn sàng"
        }
        description={
          firebaseReady
            ? "Server đã kết nối Firebase Admin và có thể gửi thông báo."
            : "Kiểm tra Firebase Service Account trên server trước khi gửi."
        }
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Tổng thiết bị"
              value={Number(stats.total || 0)}
              prefix={<MobileOutlined />}
            />
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Đang hoạt động"
              value={Number(stats.active || 0)}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Android"
              value={Number(stats.android || 0)}
            />
          </Card>
        </Col>

        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="iOS"
              value={Number(stats.ios || 0)}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Soạn thông báo"
        style={{ marginBottom: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: "general",
            link: "https://cuahangykhoa.com"
          }}
        >
          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[
              {
                required: true,
                message: "Nhập tiêu đề thông báo"
              },
              {
                max: 120,
                message: "Tiêu đề tối đa 120 ký tự"
              }
            ]}
          >
            <Input
              placeholder="Ví dụ: Sách mới đã có trên Cửa Hàng Y Khoa"
              maxLength={120}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="body"
            label="Nội dung"
            rules={[
              {
                required: true,
                message: "Nhập nội dung thông báo"
              }
            ]}
          >
            <TextArea
              rows={4}
              maxLength={500}
              showCount
              placeholder="Nhập nội dung người dùng sẽ nhìn thấy..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label="Loại thông báo"
              >
                <Select
                  options={[
                    {
                      value: "general",
                      label: "Thông báo chung"
                    },
                    {
                      value: "book",
                      label: "Sách"
                    },
                    {
                      value: "promotion",
                      label: "Khuyến mãi"
                    },
                    {
                      value: "account",
                      label: "Tài khoản"
                    }
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="link"
                label="Mở khi người dùng bấm"
              >
                <Input
                  placeholder="https://cuahangykhoa.com/san-pham/..."
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="image"
            label="URL ảnh lớn (không bắt buộc)"
          >
            <Input
              placeholder="https://cuahangykhoa.com/..."
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
            message="Deep Link"
            description="Nếu nhập link sản phẩm dạng https://cuahangykhoa.com/san-pham/ten-sach thì sau khi tích hợp FCM vào APK, bấm thông báo có thể mở thẳng trang sách trong ứng dụng."
          />

          <Space wrap>
            <Button
              onClick={testFirebase}
              loading={sending}
            >
              Kiểm tra Firebase
            </Button>

            <Button
              type="primary"
              danger
              icon={<SendOutlined />}
              onClick={confirmSend}
              loading={sending}
              disabled={!firebaseReady}
            >
              Gửi thông báo ngay
            </Button>
          </Space>
        </Form>
      </Card>

      <Card title="Lịch sử thông báo">
        <Table
          rowKey={record =>
            record.id ||
            `${record.title}-${record.create_at}`
          }
          columns={columns}
          dataSource={history}
          loading={loadingHistory}
          pagination={{
            pageSize: 10,
            showSizeChanger: true
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
}
